/**
 * Pure helpers for chart drawings (no React, no chart library): which points a drawing is
 * anchored to, how to move or reshape it, and the screen geometry of the trickier shapes.
 *
 * A drawing stores chart points { logical, price, time }. `time` is what pins it to the
 * candles (see drawingAnchors.js); anything that moves a point clears `time` so it is
 * re-pinned from the new bar position.
 */

/** The draggable anchors of a stored drawing: [{ key, point }]. */
export function anchorsOf(drawing) {
  if (!drawing) return [];
  switch (drawing.type) {
    case "hline":
      return [{ key: "price", point: { logical: null, price: drawing.price } }];
    case "vline":
      return [{ key: "logical", point: { logical: drawing.logical, price: null } }];
    case "text":
      return [{ key: "at", point: drawing.at }];
    case "rr":
      return [
        { key: "entry", point: drawing.entry },
        { key: "stop", point: drawing.stop },
        { key: "target", point: drawing.target },
      ];
    case "channel":
      return [
        { key: "start", point: drawing.start },
        { key: "end", point: drawing.end },
        // the third handle sits on the parallel line, half-way along, and sets the width
        {
          key: "offset",
          point: {
            logical: (drawing.start.logical + drawing.end.logical) / 2,
            price: (drawing.start.price + drawing.end.price) / 2 + (drawing.offset || 0),
          },
        },
      ];
    case "pitchfork":
      return [
        { key: "start", point: drawing.start },
        { key: "end", point: drawing.end },
        { key: "fork", point: drawing.fork },
      ];
    case "brush":
      return [];
    default:
      return drawing.start && drawing.end
        ? [
          { key: "start", point: drawing.start },
          { key: "end", point: drawing.end },
        ]
        : [];
  }
}

const shiftPoint = (p, dLogical, dPrice) =>
  p ? { ...p, logical: p.logical + dLogical, price: p.price + dPrice, time: null } : p;

/** The whole drawing moved by a number of bars and a price distance. */
export function moveDrawing(drawing, dLogical, dPrice) {
  if (!drawing) return drawing;
  const dl = Number.isFinite(dLogical) ? dLogical : 0;
  const dp = Number.isFinite(dPrice) ? dPrice : 0;
  switch (drawing.type) {
    case "hline":
      return { ...drawing, price: drawing.price + dp };
    case "vline":
      return { ...drawing, logical: drawing.logical + dl, time: null };
    case "text":
      return { ...drawing, at: shiftPoint(drawing.at, dl, dp) };
    case "rr": {
      const next = {
        ...drawing,
        entry: shiftPoint(drawing.entry, dl, dp),
        stop: shiftPoint(drawing.stop, dl, dp),
        target: shiftPoint(drawing.target, dl, dp),
      };
      return { ...next, rrRatio: rrRatio(next) };
    }
    case "brush":
      return { ...drawing, points: drawing.points.map((p) => shiftPoint(p, dl, dp)) };
    case "pitchfork":
      return {
        ...drawing,
        start: shiftPoint(drawing.start, dl, dp),
        end: shiftPoint(drawing.end, dl, dp),
        fork: shiftPoint(drawing.fork, dl, dp),
      };
    default:
      return {
        ...drawing,
        start: shiftPoint(drawing.start, dl, dp),
        end: shiftPoint(drawing.end, dl, dp),
      };
  }
}

/** The drawing with one anchor moved to a new chart point. */
export function setAnchor(drawing, key, point) {
  if (!drawing || !point) return drawing;
  const p = { ...point, time: null };
  if (drawing.type === "hline") return Number.isFinite(p.price) ? { ...drawing, price: p.price } : drawing;
  if (drawing.type === "vline") return Number.isFinite(p.logical) ? { ...drawing, logical: p.logical, time: null } : drawing;
  if (!Number.isFinite(p.logical) || !Number.isFinite(p.price)) return drawing;
  if (drawing.type === "channel" && key === "offset") {
    // width = price distance between the dragged point and the main line at that bar
    const { start, end } = drawing;
    const t = end.logical !== start.logical ? (p.logical - start.logical) / (end.logical - start.logical) : 0;
    const onMain = start.price + (end.price - start.price) * t;
    return { ...drawing, offset: p.price - onMain };
  }
  const next = { ...drawing, [key]: { ...(drawing[key] || {}), ...p } };
  return drawing.type === "rr" ? { ...next, rrRatio: rrRatio(next) } : next;
}

/** Reward-to-risk of a risk/reward drawing (null when the stop equals the entry). */
export function rrRatio({ entry, stop, target }) {
  const risk = Math.abs(entry.price - stop.price);
  const reward = Math.abs(target.price - entry.price);
  return risk > 1e-12 ? reward / risk : null;
}

/** Point where a ray from a through b leaves the box [0, width] x [0, height]. */
export function extendRay(a, b, width, height) {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  if (Math.abs(dx) < 1e-9 && Math.abs(dy) < 1e-9) return { ...b };
  const ts = [];
  if (dx > 0) ts.push((width - a.x) / dx);
  if (dx < 0) ts.push(-a.x / dx);
  if (dy > 0) ts.push((height - a.y) / dy);
  if (dy < 0) ts.push(-a.y / dy);
  const t = Math.max(1, Math.min(...ts.filter((v) => v > 0).concat([1e6])));
  return { x: a.x + dx * t, y: a.y + dy * t };
}

/** Andrews' Pitchfork median and parallel rails, extended to the visible plot edges. */
export function pitchforkSegments(origin, upperPivot, lowerPivot, width, height) {
  const points = [origin, upperPivot, lowerPivot];
  if (!points.every((p) => Number.isFinite(p?.x) && Number.isFinite(p?.y)) || !(width > 0 && height > 0)) return null;
  const midpoint = {
    x: (upperPivot.x + lowerPivot.x) / 2,
    y: (upperPivot.y + lowerPivot.y) / 2,
  };
  const dx = midpoint.x - origin.x;
  const dy = midpoint.y - origin.y;
  if (Math.hypot(dx, dy) < 1e-8) return null;
  const parallelEnd = (pivot) => extendRay(pivot, { x: pivot.x + dx, y: pivot.y + dy }, width, height);
  return {
    median: [origin, extendRay(origin, midpoint, width, height)],
    upper: [upperPivot, parallelEnd(upperPivot)],
    lower: [lowerPivot, parallelEnd(lowerPivot)],
  };
}

/**
 * Screen geometry of a risk/reward tool in the TradingView style: a profit zone from the
 * entry to the target and a loss zone from the entry to the stop, both spanning from the
 * entry bar to the target bar (at least `minWidth` px wide).
 */
export function rrBoxes(entry, stop, target, minWidth = 60) {
  const left = Math.min(entry.x, target.x);
  const width = Math.max(minWidth, Math.abs(target.x - entry.x));
  return {
    left,
    width,
    profit: { y: Math.min(entry.y, target.y), height: Math.abs(target.y - entry.y) },
    loss: { y: Math.min(entry.y, stop.y), height: Math.abs(stop.y - entry.y) },
  };
}

/** Distance from point p to the segment a-b, in px. */
export function distanceToSegment(p, a, b) {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len2 = dx * dx + dy * dy;
  const t = len2 ? Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / len2)) : 0;
  return Math.hypot(p.x - (a.x + dx * t), p.y - (a.y + dy * t));
}

/** Bounding box of a list of screen points: { left, top, right, bottom }. */
export function boundsOf(points) {
  const xs = points.filter(Boolean).map((p) => p.x);
  const ys = points.filter(Boolean).map((p) => p.y);
  if (!xs.length) return null;
  return { left: Math.min(...xs), top: Math.min(...ys), right: Math.max(...xs), bottom: Math.max(...ys) };
}
